'use client';

import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

// Style
import { useTranslations } from 'next-intl';
import { Button, Center, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { IRedux } from '@/_store/types';
import { useRouter } from '@/i18n/navigation';
import { EXCLUDE_STOP, TOGGLE_CHECKOUT } from '@/_store/cart/cart.slice';
import NumberToPrice from '@/_components/NumberToPrice';
import productService from '@/_service/product.service';
import Auth from '@/_components/Auth';
import { syncCartStopFlagsFromMenuProducts } from '@/_utils/cartMenuAvailability';
import getShipperById from '@/_service/shipper.service';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { useParams } from 'next/navigation';
import { getBrandHref } from '@/_lib/brand-href';

export default function Bill() {
  const [isAuthDialog, setIsAuthDialog] = useState(false);
  const [deliveryPrice, setDeliveryPrice] = useState(0);

  const { user, cartStore, common, settings } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartStore: state.cart?.cart,
      common: state.common,
      settings: state.settings.data,
      session: state.session,
    }),
    shallowEqual
  );

  const shipperId = useActiveBrand()
  const branch = common?.branches?.[shipperId]
  const router = useRouter();
  const t = useTranslations();
  const dispatch = useDispatch();
  const params = useParams()

  const cart = cartStore?.[shipperId]?.products || []

  const productIds = useMemo(
    () => String(cart?.map((product) => product.product_id)),
    [cart]
  );
  const productIdQuantity = useMemo(() => {
    const result: { [key: string]: number } = {};

    cart?.forEach((product) => {
      result[product?.product_id] = Number(product.quantity);
    });

    return result;
  }, [cart]);

  const totalPrice = useMemo(() => {
    if (cart && cart.length === 0) return 0;
    let total = 0;
    cart?.forEach((product) => {
      total += (product.price_with_discount || 0) * product.quantity;
      if (product?.order_modifiers?.length > 0) {
        for (const modifier of product.order_modifiers) {
          total +=
            +modifier.modifier_price *
            +(modifier?.modifier_quantity * product.quantity);
        }
      }
    });
    return total;
  }, [cart]);

  const onButtonClick = () => {
    if (!user) {
      setIsAuthDialog(true);
    } else {
      dispatch(TOGGLE_CHECKOUT({ shipper_id: shipperId }));
      // Navigate to brand checkout if on brand page, otherwise regular checkout
      const checkoutRoute =
        params?.brandSlug || params?.id
          ? getBrandHref(shipperId, { path: '/checkout' })
          : `/checkout`;
      router.push(checkoutRoute);
    }
  };

  const { data: menuProducts } = useQuery({
    queryKey: ['menu-products', branch?.menu_id, productIds, shipperId],
    queryFn: () =>
      productService
        .getMenuProducts({
          menu_id: branch?.menu_id || '',
          items: JSON.stringify(productIdQuantity),
          // product_ids: productIds,
        }, shipperId)
        .then((res) => res?.data),
    enabled: Boolean(
      cart?.length > 0 &&
      (!!branch?.menu_id)
    ),
  });

  const { data: shipper } = useQuery({
    queryKey: ['GET_SHIPPER_BY_ID', settings],
    queryFn: () =>
      getShipperById(settings?.shipper_id || '').then((res) => res?.data),
    enabled: !!settings?.shipper_id,
  });

  const deliveryZone = common?.deliveryZones?.[shipperId];
  const minimalPrice = useMemo(() => {
    if (deliveryZone?.id) return deliveryZone?.min_order_amount || 0;

    return shipper?.minimal_order_price || 0;
  }, [shipper?.minimal_order_price, deliveryZone?.id]);

  useEffect(() => {
    if (!menuProducts || !cart?.length) return;
    syncCartStopFlagsFromMenuProducts(dispatch, cart, menuProducts, {
      shipper_id: shipperId,
      at: new Date(),
    });
  }, [menuProducts, dispatch, shipperId]);

  // Remove product when menu is not found
  useEffect(() => {
    if (!branch?.menu_id && cartStore?.cart) {
      // Clear stopped items from all brands
      dispatch(EXCLUDE_STOP({ shipper_id: shipperId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch?.menu_id]);

  if (cart.length > 0)
    return (
      <>
        <Center w={'full'} zIndex={2} flexDir='column' bg={'background.card'}>
          {Boolean(minimalPrice && minimalPrice > totalPrice) && (
            <Flex pt={2} gap={1} color='red' fontSize={14}>
              {t('minimum_order_amount')}
              <NumberToPrice value={minimalPrice} />
            </Flex>
          )}
          <Button
            onClick={onButtonClick}
            w='full'
            size='xl'
            h='48px'
            bg='primary'
            color='primary.text'
            _hover={{ bg: 'primary.hover' }}
            rounded={12}
            disabled={Boolean(minimalPrice && minimalPrice > totalPrice)}
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            px={6}
          >
            <Text fontWeight={500} fontSize='14px'>
              {t('checkout_order')}
            </Text>
            <NumberToPrice
              value={totalPrice + deliveryPrice}
              textProps={{
                fontWeight: 700,
                fontSize: 16,
                color: 'primary.text',
              }}
              spanProps={{
                fontWeight: 700,
                fontSize: 16,
                color: 'primary.text',
              }}
            />
          </Button>
        </Center>
        <Auth
          open={isAuthDialog}
          onClose={() => setIsAuthDialog(false)}
          onLastConfirm={() =>
            router.push(getBrandHref(shipperId, { path: '/checkout' }))
          }
        />
      </>
    );

  return null;
}
